/**
 * Pripojiť výrobu k výpočtu (KOLO 208) a spojiť dve výroby (KOLO 209).
 *
 * User: *„máš jednu zákazku a v nej všetko, čo sa týka výroby. Žiaden
 * výpočet nezmizne, výrobná varianta sa len pripojí k nej."* Výpočet V
 * (cena pre zákazníka) a výroba AA z TopSolid importu boli dve zákazky.
 * Okno sa otvára z ⋮ ktorejkoľvek z nich — druhú si user vyberie, náhľad
 * zo servera (ten istý beh ako pripojenie, nezapíše sa) povie, čo sa stane.
 *
 * Spojenie výrob (KOLO 209): do KOLA 203 nevedela zákazka niesť dve časti,
 * takže časti jedného projektu dostali vlastné AA. User: *„daj mi možnosť
 * spojiť aj 2 výrobné dokopy."* Hlavné číslo ostane, druhé zmizne.
 */
import { useEffect, useState } from "react";
import { ArrowLeft, Factory, Loader2, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  usePripojitNahlad,
  usePripojitVyrobu,
  useZakazkyList,
} from "@/api/hooks/useZakazky";
import type { Pripojenie } from "@/api/schemas";
import { STAV_LABELS } from "@/components/zakazka/StavBadge";
import { formatEur, sklonuj } from "@/lib/format";
import { useDebounce } from "@/lib/useDebounce";
import { cn } from "@/lib/utils";

export interface ZakazkaNaPripojenie {
  id: string;
  cislo_zakazky: string;
  nazov?: string | null;
}

/**
 * `vyroba` = okno otvorené na výrobe (vyberá sa výpočet), `vypocet` naopak,
 * `vyroby` = spojenie s inou výrobou (KOLO 209).
 */
export type StranaPripojenia = "vypocet" | "vyroba" | "vyroby";

/**
 * Čo ⋮ zákazky ponúkne: výpočtu V bez výroby „Pripojiť výrobu", zákazke
 * z importu výroby (bez V) „Pripojiť k výpočtu"; každej zákazke s výrobou,
 * ktorá nie je výpočet V, „Spojiť s inou výrobou".
 */
export function stranyPripojenia(z: {
  cislo_zakazky: string;
  vypocet_cislo?: string | null;
  ma_vyrobu?: boolean;
}): StranaPripojenia[] {
  const jeVypocet = !!z.vypocet_cislo && z.cislo_zakazky === z.vypocet_cislo;
  if (jeVypocet) return z.ma_vyrobu ? [] : ["vypocet"];
  if (!z.ma_vyrobu) return [];
  // Výroba s pripojeným výpočtom (AA + V) už k výpočtu nepatrí
  return z.vypocet_cislo ? ["vyroby"] : ["vyroba", "vyroby"];
}

export const NAZOV_PRIPOJENIA: Record<StranaPripojenia, string> = {
  vypocet: "Pripojiť výrobu…",
  vyroba: "Pripojiť k výpočtu…",
  vyroby: "Spojiť s inou výrobou…",
};

/** Tooltip položky v ⋮. */
export const POPIS_PRIPOJENIA: Record<StranaPripojenia, string> = {
  vypocet: "Výpočet a výroba z TopSolidu do jednej zákazky — žiadny výpočet nezmizne",
  vyroba: "Výpočet a výroba z TopSolidu do jednej zákazky — žiadny výpočet nezmizne",
  vyroby:
    "Dve výroby jedného projektu (napr. AA9539 a AA9540) do jednej zákazky s časťami výroby",
};

const NADPIS: Record<StranaPripojenia, string> = {
  vypocet: "Pripojiť výrobu",
  vyroba: "Pripojiť k výpočtu",
  vyroby: "Spojiť s inou výrobou",
};

export function PripojitDialog({
  zakazka,
  strana,
  predvolena = null,
  onOpenChange,
  onPripojene,
}: {
  /** `null` = zatvorené. */
  zakazka: ZakazkaNaPripojenie | null;
  strana: StranaPripojenia;
  /** Druhá zákazka je už známa (Doplniť AA s obsadeným číslom) — rovno náhľad. */
  predvolena?: ZakazkaNaPripojenie | null;
  onOpenChange: (open: boolean) => void;
  /** Po pripojení — id zákazky, ktorá ostala (výpočet). */
  onPripojene?: (zakazkaId: string) => void;
}): JSX.Element {
  const [druha, setDruha] = useState<ZakazkaNaPripojenie | null>(predvolena);
  const [nazov, setNazov] = useState<"vypocet" | "vyroba">("vypocet");
  useEffect(() => {
    if (zakazka) setDruha(predvolena);
  }, [zakazka, predvolena]);

  function zavri(open: boolean): void {
    if (!open) {
      setDruha(null);
      setNazov("vypocet");
    }
    onOpenChange(open);
  }

  const vypocet = strana === "vypocet" ? zakazka : druha;
  const vyroba = strana === "vyroba" ? zakazka : druha;
  const cislo = zakazka?.cislo_zakazky ?? "";

  return (
    <Dialog open={zakazka !== null} onOpenChange={zavri}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{NADPIS[strana]}</DialogTitle>
          <DialogDescription>
            {strana === "vyroby"
              ? `Výroba ${cislo} a výroba z inej zákazky toho istého projektu budú jedna zákazka — každá výroba ostane časťou výroby a do Cut Rite ide pod svojím menom.`
              : strana === "vyroba"
                ? `Výroba ${cislo} sa pripojí k výpočtu — v zákazke budú výpočty aj výroba, žiadny výpočet nezmizne.`
                : `K výpočtu ${cislo} sa pripojí výroba z TopSolidu — výpočty ostanú, výroba pribudne ako záložky.`}
          </DialogDescription>
        </DialogHeader>
        {zakazka && !druha ? (
          <VyberZakazky
            druh={strana === "vyroba" ? "vypocet" : "vyroba"}
            okrem={zakazka.id}
            prazdne={
              strana === "vyroby" ? "Žiadna iná zákazka s výrobou sa nenašla." : undefined
            }
            onVyber={setDruha}
          />
        ) : null}
        {strana === "vyroby" && zakazka && druha ? (
          <NahladSpojenia
            tato={zakazka}
            druha={druha}
            onSpat={() => setDruha(null)}
            onHotovo={(id) => {
              zavri(false);
              onPripojene?.(id);
            }}
          />
        ) : strana !== "vyroby" && vypocet && vyroba ? (
          <Nahlad
            vypocet={vypocet}
            vyroba={vyroba}
            nazov={nazov}
            onNazov={setNazov}
            onSpat={() => setDruha(null)}
            onHotovo={(id) => {
              zavri(false);
              onPripojene?.(id);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export function VyberZakazky({
  druh,
  okrem,
  prazdne,
  onVyber,
}: {
  /** Bez druhu všetky zákazky (Vložiť regál do zákazky, KOLO 237). */
  druh?: "vypocet" | "vyroba";
  okrem?: string;
  /** Veta, keď sa nič nenašlo — inak podľa druhu. */
  prazdne?: string;
  onVyber: (z: ZakazkaNaPripojenie) => void;
}): JSX.Element {
  const [hladat, setHladat] = useState("");
  const hladatPomaly = useDebounce(hladat.trim(), 250);
  const q = useZakazkyList({
    druh,
    search: hladatPomaly || undefined,
    per_page: 30,
    sort: "-updated_at",
  });
  const zakazky = (q.data?.data ?? []).filter((z) => z.id !== okrem);
  const co = druh === "vypocet" ? "výpočet" : druh === "vyroba" ? "zákazku s výrobou" : "zákazku";

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          autoFocus
          value={hladat}
          onChange={(e) => setHladat(e.target.value)}
          placeholder={`Hľadať ${co} — číslo alebo názov`}
          aria-label={`Hľadať ${co}`}
          className="pl-8"
        />
      </div>
      {q.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : q.isError ? (
        <p className="text-sm text-destructive">{extractErrorMessage(q.error)}</p>
      ) : zakazky.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {prazdne ??
            (druh === "vypocet"
              ? "Žiadny výpočet bez výroby sa nenašiel."
              : druh === "vyroba"
                ? "Žiadna zákazka s výrobou bez výpočtu sa nenašla."
                : "Žiadna zákazka sa nenašla.")}
        </p>
      ) : (
        <ul className="max-h-80 divide-y overflow-y-auto rounded-md border">
          {zakazky.map((z) => (
            <li key={z.id}>
              <button
                type="button"
                onClick={() => onVyber(z)}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/60"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{z.nazov || "Bez názvu"}</div>
                  <div className="text-xs text-muted-foreground">
                    <span className="font-mono">{z.cislo_zakazky}</span>
                    {z.varianty_nazvy?.length ? ` · ${z.varianty_nazvy.join(", ")}` : ""}
                  </div>
                </div>
                <span className="shrink-0 font-mono text-xs text-muted-foreground">
                  {formatEur(z.cena_celkom_s_dph)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Čo sa presunie (KOLO 212): „1 odrezok", nie „1 odrezkov"; nuly vynechá
 * („Presunie sa: 1 odrezok.", nie „0× odoslanie do Cut Rite, 1 odrezkov
 * a 0 objednávok.").
 */
export function vetaPresunu(n: {
  odoslani_cutrite: number;
  odrezkov: number;
  objednavok: number;
}): string {
  const casti = [
    n.odoslani_cutrite > 0 ? `${n.odoslani_cutrite}× odoslanie do Cut Rite` : null,
    n.odrezkov > 0
      ? `${n.odrezkov} ${sklonuj(n.odrezkov, ["odrezok", "odrezky", "odrezkov"])}`
      : null,
    n.objednavok > 0
      ? `${n.objednavok} ${sklonuj(n.objednavok, ["objednávka", "objednávky", "objednávok"])}`
      : null,
  ].filter((c): c is string => c !== null);
  const zoznam =
    casti.length > 1
      ? `${casti.slice(0, -1).join(", ")} a ${casti[casti.length - 1]}`
      : (casti[0] ?? "");
  return `Presunie sa: ${zoznam}.`;
}

/** Veta o cene — pripojenie ju meniť nemá; keď sa pohne, povie prečo. */
export function vetaCeny(n: Pripojenie): string {
  const pred = n.cena_vypoctu_pred;
  const po = n.cena_vypoctu_po;
  if (pred == null) return `Cena pre zákazníka: ${formatEur(po)}.`;
  if (pred === po || Number(pred) === Number(po)) {
    return `Cena pre zákazníka ostane ${formatEur(po)}.`;
  }
  return `Cena pre zákazníka sa zmení z ${formatEur(pred)} na ${formatEur(po)}.`;
}

function Nahlad({
  vypocet,
  vyroba,
  nazov,
  onNazov,
  onSpat,
  onHotovo,
}: {
  vypocet: ZakazkaNaPripojenie;
  vyroba: ZakazkaNaPripojenie;
  nazov: "vypocet" | "vyroba";
  onNazov: (n: "vypocet" | "vyroba") => void;
  onSpat: () => void;
  onHotovo: (zakazkaId: string) => void;
}): JSX.Element {
  const nahlad = usePripojitNahlad(vypocet.id, { vyroba_id: vyroba.id, nazov });
  const pripoj = usePripojitVyrobu();
  const n = nahlad.data?.data;

  async function potvrd(): Promise<void> {
    try {
      const r = await pripoj.mutateAsync({ cielId: vypocet.id, vyroba_id: vyroba.id, nazov });
      toast({
        title: "Výroba pripojená",
        description: `${r.data.cislo_po} je teraz jedna zákazka — výpočty aj výroba.`,
      });
      onHotovo(r.data.zakazka_id);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Pripojenie neprešlo",
        description: extractErrorMessage(e),
      });
    }
  }

  return (
    <div className="space-y-3 text-sm">
      {nahlad.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : nahlad.isError ? (
        <p className="text-destructive" role="alert">
          {extractErrorMessage(nahlad.error)}
        </p>
      ) : n ? (
        <>
          <p>
            Do výpočtu <span className="font-mono">{n.cislo_pred}</span>{" "}
            {n.nazov_pred ? `(${n.nazov_pred})` : ""} pribudne výroba zo zákazky{" "}
            <span className="font-mono">{n.cislo_po}</span>:
          </p>
          <ul className="space-y-1 rounded-md border p-2" aria-label="Pripojená výroba">
            {(n.varianty ?? []).map((v) => (
              <li key={v.id} className="flex items-center gap-2">
                <Factory className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                <span className="font-medium">{v.nazov}</span>
                {v.nazov !== v.povodny_nazov ? (
                  <span className="text-xs text-muted-foreground">(bola „{v.povodny_nazov}“)</span>
                ) : null}
                <span className="ml-auto text-xs text-muted-foreground">
                  {v.zostav} zostáv · {v.dielcov} dielcov
                </span>
              </li>
            ))}
          </ul>
          <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground">
            <li>
              Číslo zákazky {n.cislo_pred} → <span className="font-mono">{n.cislo_po}</span>
              {n.vypocet_cislo ? `, ${n.vypocet_cislo} ostane ako číslo výpočtu` : ""}.
            </li>
            <li>
              Stav {STAV_LABELS[n.stav_pred]} → {STAV_LABELS[n.stav_po]}.
            </li>
            <li>
              {vetaCeny(n)} Výroba spolu {formatEur(n.cena_vyroby_po)}.
              {(n.ceny_materialu_ustupia ?? []).length > 0
                ? ` Upravené ceny materiálu z výroby (${(n.ceny_materialu_ustupia ?? []).join(", ")}) ustúpia cenám výpočtu.`
                : ""}
            </li>
            {n.odoslani_cutrite + n.odrezkov + n.objednavok > 0 ? (
              <li>{vetaPresunu(n)}</li>
            ) : null}
            <li>
              Zákazka <span className="font-mono">{n.cislo_po}</span> ako samostatná z prehľadu
              zmizne.
            </li>
          </ul>
          <fieldset className="space-y-1">
            <legend className="font-medium">Názov zákazky</legend>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="nazov-pripojenia"
                checked={nazov === "vypocet"}
                onChange={() => onNazov("vypocet")}
              />
              {n.nazov_pred || "bez názvu"}{" "}
              <span className="text-xs text-muted-foreground">(z výpočtu)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="nazov-pripojenia"
                checked={nazov === "vyroba"}
                onChange={() => onNazov("vyroba")}
              />
              {n.nazov_vyroby || "bez názvu"}{" "}
              <span className="text-xs text-muted-foreground">(z výroby)</span>
            </label>
          </fieldset>
        </>
      ) : null}
      <DialogFooter className="gap-2 sm:justify-between">
        <Button type="button" variant="ghost" onClick={onSpat}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Iná zákazka
        </Button>
        <Button type="button" onClick={() => void potvrd()} disabled={!n || pripoj.isPending}>
          {pripoj.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
          Pripojiť
        </Button>
      </DialogFooter>
    </div>
  );
}

/** Mená častí z okna — prázdne alebo dvakrát to isté server nepustí. */
export function chybaMienCasti(mena: Record<string, string>): string | null {
  const videne = new Set<string>();
  for (const meno of Object.values(mena)) {
    const ciste = meno.trim();
    if (!ciste) return "Časť výroby musí mať meno.";
    const kluc = ciste.toLocaleLowerCase("sk");
    if (videne.has(kluc)) return `Dve časti výroby nemôžu mať to isté meno „${ciste}“.`;
    videne.add(kluc);
  }
  return null;
}

/** Cena po spojení — výroba spolu oboch, ak cenou nie je aktívny výpočet. */
export function vetaCenySpojenia(n: Pripojenie): string {
  const veta = `Cena zákazky ${formatEur(n.cena_zakazky_pred)} + ${formatEur(n.cena_zdroja_pred)} → ${formatEur(n.cena_zakazky_po)}`;
  const sucet = Number(n.cena_zakazky_pred ?? 0) + Number(n.cena_zdroja_pred ?? 0);
  if (n.cena_vypoctu_po != null && Math.abs(Number(n.cena_zakazky_po ?? 0) - sucet) > 0.005) {
    return `${veta} — cenou je aktívny výpočet.`;
  }
  return `${veta}.`;
}

const bezCsv = (meno: string): string => meno.replace(/\.csv$/i, "");

function NahladSpojenia({
  tato,
  druha,
  onSpat,
  onHotovo,
}: {
  tato: ZakazkaNaPripojenie;
  druha: ZakazkaNaPripojenie;
  onSpat: () => void;
  onHotovo: (zakazkaId: string) => void;
}): JSX.Element {
  // Hlavné číslo ostane (cieľ), druhé zmizne — prepnutie vymení strany
  const [hlavnaId, setHlavnaId] = useState(tato.id);
  // Názov ide s hlavným číslom, kým ho user nezvolí sám
  const [nazovVolba, setNazovVolba] = useState<string | null>(null);
  const nazovId = nazovVolba ?? hlavnaId;
  const ciel = hlavnaId === tato.id ? tato : druha;
  const zdroj = hlavnaId === tato.id ? druha : tato;
  // Mená častí podľa id varianty — `null` = predvolené (meno zákazky)
  const [mena, setMena] = useState<Record<string, string> | null>(null);
  const menaPomaly = useDebounce(mena, 300);
  const [menaNahladu, setMenaNahladu] = useState<Record<string, string> | null>(null);
  useEffect(() => {
    // Neplatné mená do náhľadu nejdú — 422 by zakryla celé okno
    if (menaPomaly === null || !chybaMienCasti(menaPomaly)) setMenaNahladu(menaPomaly);
  }, [menaPomaly]);
  const chybaMien = mena ? chybaMienCasti(mena) : null;
  const nazov = nazovId === ciel.id ? "ciel" : "zdroj";

  const nahlad = usePripojitNahlad(ciel.id, {
    vyroba_id: zdroj.id,
    nazov,
    ...(menaNahladu ? { mena_casti: menaNahladu } : {}),
  });
  const pripoj = usePripojitVyrobu();
  // Náhľad druhého smeru (pred prepnutím hlavného čísla) sa neukazuje —
  // povedal by, že zmizne číslo, ktoré práve ostáva
  const n = nahlad.data?.data.zakazka_id === ciel.id ? nahlad.data.data : undefined;
  const casti = (n?.varianty ?? []).filter((v) => v.je_vyrobna);
  const vypocty = (n?.varianty ?? []).filter((v) => !v.je_vyrobna);
  const zmiznute = (n?.zmiznute_cisla ?? []).join(", ");

  function premenuj(id: string, meno: string): void {
    setMena({
      ...Object.fromEntries(casti.map((c) => [c.id, mena?.[c.id] ?? c.nazov])),
      [id]: meno,
    });
  }

  async function potvrd(): Promise<void> {
    try {
      const r = await pripoj.mutateAsync({
        cielId: ciel.id,
        vyroba_id: zdroj.id,
        nazov,
        ...(mena ? { mena_casti: mena } : {}),
      });
      toast({
        title: "Výroby spojené",
        description: `${r.data.cislo_po} je teraz jedna zákazka — ${casti.length} časti výroby.`,
      });
      onHotovo(r.data.zakazka_id);
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Spojenie neprešlo",
        description: extractErrorMessage(e),
      });
    }
  }

  return (
    <div className="space-y-3 text-sm">
      <fieldset className="space-y-1">
        <legend className="font-medium">Hlavné číslo</legend>
        {[tato, druha].map((z) => (
          <label key={z.id} className="flex items-center gap-2">
            <input
              type="radio"
              name="hlavne-cislo"
              checked={hlavnaId === z.id}
              onChange={() => setHlavnaId(z.id)}
            />
            <span className="font-mono">{z.cislo_zakazky}</span>
            {z.nazov ? <span className="text-muted-foreground">{z.nazov}</span> : null}
          </label>
        ))}
      </fieldset>
      {tato.nazov !== druha.nazov ? (
        <fieldset className="space-y-1">
          <legend className="font-medium">Názov zákazky</legend>
          {[tato, druha].map((z) => (
            <label key={z.id} className="flex items-center gap-2">
              <input
                type="radio"
                name="nazov-spojenia"
                checked={nazovId === z.id}
                onChange={() => setNazovVolba(z.id)}
              />
              {z.nazov || "bez názvu"}{" "}
              <span className="text-xs text-muted-foreground">(z {z.cislo_zakazky})</span>
            </label>
          ))}
        </fieldset>
      ) : null}
      {nahlad.isError ? (
        <p className="text-destructive" role="alert">
          {extractErrorMessage(nahlad.error)}
        </p>
      ) : !n ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <div className={cn("space-y-3", nahlad.isFetching && "opacity-60")}>
          <div className="space-y-1">
            <p className="font-medium">Časti výroby</p>
            <ul className="space-y-1.5 rounded-md border p-2" aria-label="Časti výroby">
              {casti.map((v) => {
                const hodnota = mena?.[v.id] ?? v.nazov;
                return (
                  <li key={v.id} className="flex flex-wrap items-center gap-2">
                    <Factory className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                    <Input
                      value={hodnota}
                      maxLength={50}
                      onChange={(e) => premenuj(v.id, e.target.value)}
                      aria-label={`Meno časti zo zákazky ${v.zo_zakazky}`}
                      className="h-8 w-44 sm:w-56"
                    />
                    <span className="font-mono text-xs text-muted-foreground">
                      {v.zo_zakazky}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {v.zostav} zostáv · {v.dielcov} dielcov
                    </span>
                    {menaNahladu?.[v.id]?.trim() === hodnota.trim() &&
                    v.nazov !== hodnota.trim() ? (
                      <span className="w-full pl-6 text-xs text-muted-foreground">
                        uloží sa ako „{v.nazov}“ — meno je obsadené
                      </span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {chybaMien ? (
              <p className="text-destructive" role="alert">
                {chybaMien}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Meno časti ide do Cut Rite ako {n.cislo_po}_&lt;meno&gt; — predvolené je meno
                zákazky, zoznam v Cut Rite tak ostane ten istý.
              </p>
            )}
          </div>
          <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground">
            {zmiznute ? (
              <li>
                <span className="font-mono">{zmiznute}</span> zmizne — ďalšie exporty z Tandem
                Zostáv posielaj pod <span className="font-mono">{n.cislo_po}</span> s dovetkom (
                <span className="font-mono">{n.cislo_po}_&lt;časť&gt;</span>). Hľadanie podľa{" "}
                {zmiznute} nájde {n.cislo_po}.
              </li>
            ) : null}
            {vypocty.length > 0 ? (
              <li>
                Pribudnú výpočty {vypocty.map((v) => v.nazov).join(", ")} zo zákazky{" "}
                {vypocty[0]?.zo_zakazky}.
              </li>
            ) : null}
            <li>
              {n.stav_pred === n.stav_po
                ? `Stav ostane ${STAV_LABELS[n.stav_po]}.`
                : `Stav ${STAV_LABELS[n.stav_pred]} → ${STAV_LABELS[n.stav_po]}.`}
            </li>
            <li>{vetaCenySpojenia(n)}</li>
            {(n.zoznamy_cutrite ?? []).map((z) => (
              <li key={z.stare}>
                V Cut Rite ostane zoznam <span className="font-mono">{bezCsv(z.stare)}</span> —
                ďalšie odoslanie pôjde ako{" "}
                <span className="font-mono">{bezCsv(z.nove)}</span>, starý tam zmaž.
              </li>
            ))}
            {(n.objednavky_spor ?? []).length > 0 ? (
              <li>
                Objednávka materiálu {(n.objednavky_spor ?? []).join(", ")} je v oboch
                zákazkách — ostane riadok {n.cislo_po}, poznámka povie, čo mal {zmiznute}.
              </li>
            ) : null}
            {(n.ceny_materialu_ustupia ?? []).length > 0 ? (
              <li>
                Upravené ceny materiálu z {zmiznute} (
                {(n.ceny_materialu_ustupia ?? []).join(", ")}) ustúpia cenám {n.cislo_po}.
              </li>
            ) : null}
            {n.odoslani_cutrite + n.odrezkov + n.objednavok > 0 ? (
              <li>{vetaPresunu(n)}</li>
            ) : null}
          </ul>
        </div>
      )}
      <DialogFooter className="gap-2 sm:justify-between">
        <Button type="button" variant="ghost" onClick={onSpat}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          Iná zákazka
        </Button>
        <Button
          type="button"
          onClick={() => void potvrd()}
          disabled={!n || nahlad.isFetching || !!chybaMien || pripoj.isPending}
        >
          {pripoj.isPending || nahlad.isFetching ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : null}
          Spojiť
        </Button>
      </DialogFooter>
    </div>
  );
}
